import React, { useState, useEffect } from "react";
import pt from "react-phone-input-2/lang/pt.json";
import PhoneInput from "react-phone-input-2";
import {
  ContactWithOtherUser,
  MyContactAlreadyExists,
  ErrorMessage,
} from "./helpers";
import {
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  FormHelperText,
  Typography,
} from "@material-ui/core";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { ButtonAlert, Container, Field } from "./styles";

import { IconModal } from "../../components";
import { API } from "../../../../shared/services";
import { Autocomplete } from "@material-ui/lab";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useMainState } from "modules/app/contexts";

export default function NewCustomer() {
  const [errorRequest, setErrorRequest] = useState();
  const [cantCreate, setCantCreate] = useState(true);
  const [users, setUsers] = useState([]);
  const [alert, setAlert] = useState(false);
  const [country, setCountry] = useState("BR");
  const { t } = useTranslation(["NewCustomer", "common"]);
  const history = useHistory();
  const { company, leadSources, channels } = useMainState();

  useEffect(() => {
    if (company) {
      const [_language, newCountry] = company.locale.split("-");
      setCountry(newCountry || "BR");
    }
  }, [company]);

  useEffect(() => {
    async function getUsers() {
      const result = await API.get("/users");

      result && setUsers(result.data);
    }

    getUsers();
  }, []);

  async function checkIfContactExist({ phone, email }, errors) {
    if (errors.emailContact || errors.phoneContact) {
      toast.error("Contato inválido");
      return;
    }

    const result = await API.get("customers/check_contact", {
      params: {
        phone: phone,
        email: email,
      },
    });

    const check = result.data;

    if (check.exists && check.my_customer) {
      setAlert(() => <MyContactAlreadyExists id={check.customer_id} />);
    } else if (check.exists && !check.my_customer) {
      setAlert(() => <ContactWithOtherUser owner_name={check.owner_name} />);
    } else {
      toast.success("Dados de contato liberado");

      setAlert(null);
      setErrorRequest(null);
      setCantCreate(false);
    }
  }

  const formikSettings = {
    initialValues: {
      nameContact: "",
      emailContact: "",
      phoneContact: "",

      nameProduct: "",
      priceProduct: "",
      urlProduct: "",
      channel: null,
      leadSource: null,

      user: null,
    },
    validationSchema: Yup.object({
      nameContact: Yup.string(t("common:errors.invalid"))
        .required(t("common:errors.required"))
        .nullable(),
      nameProduct: Yup.string(t("common:errors.invalid"))
        .required(t("common:errors.required"))
        .nullable(),
      emailContact: Yup.string(t("common:errors.invalid"))
        .email(t("common:errors.invalid"))
        .nullable(),
      phoneContact: Yup.string(t("common:errors.invalid"))
        .test(
          "isRequiredPhoneNumber",
          t("common:errors.required"),
          (value, testContext) => {
            return (
              (value && value.replace(/^\+\d*\s*/, "")) ||
              testContext.parent.emailContact
            );
          }
        )
        .nullable(),
      urlProduct: Yup.string(t("common:errors.invalid"))
        .url(t("common:errors.invalid"))
        .nullable(),
      user: Yup.mixed(t("common:errors.invalid"))
        .required(t("common:errors.required"))
        .nullable(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const customer = {
        contact: {
          name: values.nameContact,
          email: values.emailContact,
          phone: values.phoneContact,
        },
        products: [
          {
            name: values.nameProduct,
            price: values.priceProduct,
            url: values.urlProduct,
            lead_source_id: values.leadSource?.id,
            channel_id: values.channel?.id,
          },
        ],
        user: {
          id: values.user.id,
        },
        company: {
          id: values.user.company_id,
        },
      };

      API.post("/customers", { customer })
        .then((result) => {
          toast.success("Criado com sucesso");
          setErrorRequest(null);
          history.push(`/app/chat/${result.data.id}`);
        })
        .catch((error) => {
          toast.error("Erro ao criar contato");

          const data = error.response.data;
          setErrorRequest(ErrorMessage(data));

          setSubmitting(false);
        });
    },
  };

  return (
    <Formik {...formikSettings}>
      {({
        isSubmitting,
        values,
        handleChange,
        handleSubmit,
        errors,
        setFieldValue,
        validateForm,
      }) => (
        <Container>
          <IconModal open={alert} setOpen={setAlert} title="Ops">
            {alert}
          </IconModal>

          <Typography style={{ fontWeight: "bold", fontSize: 22 }}>
            {t("title")}
          </Typography>

          <Divider style={{ marginBottom: 30 }} />

          {!!errorRequest && errorRequest}

          <FormControl component="fieldset" variant="outlined">
            <FormLabel component="legend">Contato</FormLabel>

            <Field
              name="nameContact"
              variant="outlined"
              label={t("labels.contact.name")}
              value={values.nameContact}
              helperText={errors.nameContact}
              onChange={handleChange}
            />

            <Field
              variant="outlined"
              label={t("labels.contact.email")}
              helperText={errors.emailContact}
              name="emailContact"
              value={values.emailContact}
              onChange={handleChange}
            />

            <PhoneInput
              enableSearch
              country={country.toLowerCase()}
              value={values.phoneContact}
              specialLabel={t("labels.contact.phone")}
              inputProps={{ name: "phoneContact" }}
              onChange={(phoneNumber, country, e) => {
                handleChange(e);
              }}
              isValid={!errors.phoneContact}
              localization={pt}
            />
            {errors.phoneContact && (
              <FormHelperText error={true}>
                {errors.phoneContact}
              </FormHelperText>
            )}

            {cantCreate && (
              <ButtonAlert
                color="primary"
                variant="contained"
                onClick={() =>
                  validateForm().then((errors) => {
                    return checkIfContactExist(
                      {
                        phone: values.phoneContact,
                        email: values.emailContact,
                      },
                      errors
                    );
                  })
                }
              >
                Verificar se contato já existe
              </ButtonAlert>
            )}

          </FormControl>
          {!cantCreate && (
            <>
              <FormControl component="fieldset" variant="outlined">
                <FormLabel component="legend">Produto</FormLabel>

                <Field
                  variant="outlined"
                  label={t("labels.product.name")}
                  value={values.nameProduct}
                  name="nameProduct"
                  helperText={errors.nameProduct}
                  onChange={handleChange}
                  disabled={cantCreate}
                />

                <Field
                  type="number"
                  variant="outlined"
                  label={t("labels.product.price")}
                  name="priceProduct"
                  values={values.priceProduct}
                  onChange={handleChange}
                  disabled={cantCreate}
                />

                <Field
                  type="url"
                  variant="outlined"
                  label={t("labels.product.url")}
                  name="urlProduct"
                  helperText={errors.urlProduct}
                  values={values.urlProduct}
                  onChange={handleChange}
                  disabled={cantCreate}
                />

                <Autocomplete
                  id="channel"
                  options={channels.filter((item) => item.form_visible)}
                  getOptionLabel={(channel) => channel.name}
                  fullWidth
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(_event, channel) =>
                    setFieldValue("channel", channel)
                  }
                  renderInput={(params) => (
                    <Field
                      {...params}
                      label={t("labels.product.channel")}
                      variant="outlined"
                      helperText={errors.channel}
                      disabled={cantCreate}
                    />
                  )}
                />

                <Autocomplete
                  id="leadSource"
                  options={leadSources}
                  getOptionLabel={(leadSource) => leadSource.name}
                  fullWidth
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(_event, leadSource) =>
                    setFieldValue("leadSource", leadSource)
                  }
                  renderInput={(params) => (
                    <Field
                      {...params}
                      label={t("labels.product.lead_source")}
                      variant="outlined"
                      helperText={errors.leadSource}
                      disabled={cantCreate}
                    />
                  )}
                />
              </FormControl>

              <FormControl component="fieldset" variant="outlined">
                <FormLabel component="legend"> Usuário </FormLabel>

                <Autocomplete
                  id="user"
                  options={users}
                  getOptionLabel={(user) => user.name}
                  fullWidth
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(_event, user) => setFieldValue("user", user)}
                  renderInput={(params) => (
                    <Field
                      {...params}
                      label="Usuário"
                      variant="outlined"
                      helperText={errors.user}
                      disabled={cantCreate}
                    />
                  )}
                />
              </FormControl>

              <ButtonAlert
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || cantCreate}
                variant="contained"
              >
                {isSubmitting ? <CircularProgress /> : "Criar contato"}
              </ButtonAlert>
            </>
          )}
        </Container>
      )}
    </Formik>
  );
}
